import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { ActivityEvent, ActivitySession } from '@igf-minar/shared'
import { api } from '../lib/api'
import { useCurrentUser, isAdminRole, isSuperAdminRole } from '../lib/auth'

export const Route = createFileRoute('/admin/activity')({
  component: AdminActivityPage,
})

const EVENT_LABEL: Record<string, string> = {
  login: 'Innskráning',
  logout: 'Útskráning',
  login_failed: 'Misheppnuð innskráning',
  page_view: 'Síðuskoðun',
  click: 'Smellur',
  admin_action: 'Stjórnandaaðgerð',
  export: 'Útflutningur',
  password_changed: 'Lykilorði breytt',
  feedback_sent: 'Athugasemd send',
}

function AdminActivityPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const isSuper = isSuperAdminRole(me)

  const today = new Date().toISOString().slice(0, 10)
  const twoWeeksAgo = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10)
  const [from, setFrom] = useState(twoWeeksAgo)
  const [to, setTo] = useState(today)

  const summary = useQuery({
    queryKey: ['admin', 'activity', 'summary'],
    queryFn: () => api.admin.activity.summary(),
    enabled: isAdminRole(me),
  })

  const sessions = useQuery({
    queryKey: ['admin', 'activity', 'sessions', from, to],
    queryFn: () =>
      api.admin.activity.sessions({
        from: from ? `${from}T00:00:00Z` : undefined,
        to: to ? `${to}T23:59:59Z` : undefined,
        limit: 200,
      }),
    enabled: isAdminRole(me),
  })

  if (meLoading) return <PageShell><p className="text-[var(--muted)]">Hleð…</p></PageShell>

  if (!isAdminRole(me)) {
    return (
      <PageShell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">Þú þarft admin-réttindi til að sjá virknilogg.</p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--fg)]">Virkni</h1>
        <p className="text-sm text-[var(--muted)] mt-1">
          {isSuper
            ? 'Superadmin: öll virkni í kerfinu, þ.m.t. admin- og stjórnandaaðgerðir.'
            : 'Admin: innskráningar og virkni viðskiptavina þinna.'}
        </p>
      </div>

      {summary.data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Innskráningar (14 d)" value={summary.data.totalLogins} />
          <StatCard label="Virkir notendur" value={summary.data.uniqueUsers} />
          <StatCard label="Lotur alls" value={summary.data.totalSessions} />
          <StatCard label="Meðal-lengd lotu" value={formatDuration(summary.data.avgDurationSeconds)} />
        </div>
      )}

      {summary.data && summary.data.daily.length > 0 && (
        <div className="bg-[var(--surface)] shadow-[var(--elev-sm)] rounded-xl border border-[var(--border)] p-4 mb-6">
          <h2 className="text-sm font-medium text-[var(--fg-soft)] mb-3">Innskráningar á dag (14 d)</h2>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={summary.data.daily} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(d: string) => d.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip
                  cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                  contentStyle={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    color: 'var(--fg)',
                    fontSize: 12,
                  }}
                  labelStyle={{ color: 'var(--muted)' }}
                />
                <Bar dataKey="logins" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-[var(--muted)]">Frá</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={dateCls()} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--muted)]">Til</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={dateCls()} />
        </div>
      </div>

      {sessions.isLoading && <p className="text-[var(--muted)]">Sæki lotur…</p>}
      {sessions.error && <p className="text-red-500">{(sessions.error as Error).message}</p>}

      {sessions.data && (
        <div className="bg-[var(--surface)] shadow-[var(--elev-sm)] rounded-xl overflow-hidden border border-[var(--border)]">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--surface-2)]">
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--muted)]">
                  <th className="px-4 py-3 font-medium">Notandi</th>
                  {isSuper && <th className="px-4 py-3 font-medium">Hlutverk</th>}
                  <th className="px-4 py-3 font-medium">Innskráning</th>
                  <th className="px-4 py-3 font-medium">Lengd</th>
                  <th className="px-4 py-3 font-medium text-right">Síður</th>
                  <th className="px-4 py-3 font-medium text-right">Smellir</th>
                  <th className="px-4 py-3 font-medium">IP</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {sessions.data.sessions.length === 0 && (
                  <tr>
                    <td colSpan={isSuper ? 8 : 7} className="px-4 py-6 text-center text-[var(--faint)]">
                      Engar lotur á þessu tímabili.
                    </td>
                  </tr>
                )}
                {sessions.data.sessions.map((s) => (
                  <SessionRow key={s.id} session={s} isSuper={isSuper} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  )
}

function SessionRow({ session, isSuper }: { session: ActivitySession; isSuper: boolean }) {
  const [open, setOpen] = useState(false)
  const detail = useQuery({
    queryKey: ['admin', 'activity', 'session', session.id],
    queryFn: () => api.admin.activity.session(session.id),
    enabled: open,
  })

  return (
    <>
      <tr className="hover:bg-[var(--surface-2)]">
        <td className="px-4 py-3 text-[var(--fg)]">{session.actorEmail ?? '—'}</td>
        {isSuper && (
          <td className="px-4 py-3">
            <RoleBadge role={session.actorRole} />
          </td>
        )}
        <td className="px-4 py-3 text-[var(--muted)]">{formatDateTime(session.startedAt)}</td>
        <td className="px-4 py-3 text-[var(--muted)]">
          {formatDuration(session.durationSeconds)}
          {session.endedAt === null && <span className="ml-1 text-emerald-500 text-xs">• virk</span>}
        </td>
        <td className="px-4 py-3 text-right text-[var(--muted)]">{session.pageViewCount}</td>
        <td className="px-4 py-3 text-right text-[var(--muted)]">{session.clickCount}</td>
        <td className="px-4 py-3 text-[var(--faint)] text-xs">{session.ip ?? '—'}</td>
        <td className="px-4 py-3 text-right">
          <button onClick={() => setOpen((v) => !v)} className="text-xs text-[var(--brand)] hover:underline">
            {open ? 'Fela' : 'Skoða'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={isSuper ? 8 : 7} className="px-4 py-3 bg-[var(--surface-2)]">
            {detail.isLoading && <p className="text-xs text-[var(--muted)]">Sæki atburði…</p>}
            {detail.data && <EventTimeline events={detail.data.events} />}
          </td>
        </tr>
      )}
    </>
  )
}

function EventTimeline({ events }: { events: ActivityEvent[] }) {
  if (events.length === 0) return <p className="text-xs text-[var(--faint)]">Engir atburðir.</p>
  return (
    <ol className="space-y-1">
      {events.map((e) => (
        <li key={e.id} className="flex items-baseline gap-3 text-xs">
          <span className="text-[var(--faint)] tabular-nums w-20 shrink-0">{formatTime(e.createdAt)}</span>
          <span className="font-medium text-[var(--fg-soft)] w-40 shrink-0">
            {EVENT_LABEL[e.eventType] ?? e.eventType}
          </span>
          <span className="text-[var(--muted)] truncate">
            {e.label ? e.label : e.path ?? ''}
            {e.path && e.label ? <span className="text-[var(--faint)]"> · {e.path}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  )
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === 'superadmin'
      ? 'bg-purple-50 text-purple-700 ring-purple-200'
      : role === 'admin'
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
        : role === 'user'
          ? 'bg-gray-100 text-gray-600 ring-gray-200'
          : 'bg-amber-50 text-amber-700 ring-amber-200'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {role}
    </span>
  )
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-[var(--surface)] shadow-[var(--elev-sm)] rounded-xl border border-[var(--border)] p-4">
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="mt-1 text-2xl font-bold text-[var(--fg)]">{value}</div>
    </div>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}

function dateCls() {
  return 'mt-1 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-1.5 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)]'
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 1) return '—'
  const s = Math.round(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}klst ${m}m`
  if (m > 0) return `${m}m ${sec}s`
  return `${sec}s`
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('is-IS', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('is-IS', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}
